"use client"
import { createContext, useContext, useState, type ReactNode } from "react"
import {
  POLICY_ICONS,
  POLICY_STATUS_LABELS,
  POLICY_STATUS_ORDER,
  type Policy,
  type PolicyStatus,
} from "./policiesData"
import {
  HUD87_POLICY_STATUS_APPEARANCE,
  type PolicyItemAppearance,
  type PolicySquareHighlight,
} from "./policies-panel/policyAppearance"
import { useGroupedPoliciesForActiveDate } from "./useGroupedPoliciesForActiveDate"
import { HudHoverDescription, HudHoverTitle } from "../hudHoverDetailHeader"
import {
  POLICY_DESCRIPTIONS,
  POLICY_EXTENDED_DESCRIPTIONS,
} from "./policyDescriptions"
import {
  HoverSwapFadeOverlay,
  hoverSwapHiddenContentStyle,
  useHoverSwapSequentialFade,
} from "../hoverSwapFade"
import { useHoverSwapsDisabled } from "../hoverSwapsDisabled"
import type { PolicyMapDesignId } from "./policyMapHoverDetail"

// ─── Editable content ────────────────────────────────────────────────────────
// Type treatment for the inline policy detail that takes over the swap
// target's slot on hover. Name and status render in the HUD's mono voice; the
// description drops to sans for readability at small sizes.
export const POLICY_HOVER_DETAIL_STYLE = {
  nameFontSize: "11px",
  descriptionFontSize: "10px",
  descriptionLineHeight: 1.45,
}

// How the policy hover detail renders (undefined = the stock treatment above).
// One tab per treatment on this branch (tabs 23–27) — set via PolicyHoverSwap's
// detailTreatment prop; knobs live in the per-treatment consts just below.
export type PolicyDetailTreatment =
  | "big-square-aside"
  | "red-plate"
  | "more-text"
  | "more-text-no-status"
  | "more-text-status-top"
  | "headline"
  | "red-frame"

// Tab 23 "big-square-aside" — a poster-scale policy square (same per-status
// treatment as the 16px panel boxes, icon included) anchors the detail's left
// edge, with name / status / description in a column beside it.
export const POLICY_DETAIL_BIG_SQUARE_STYLE = {
  squareSizePx: 46,
  squareRadiusPx: 8,
  squareIconSizePx: 26,
  squareStrokeWidthPx: 2,
  squareIconStrokeWidth: 1.9,
  textGapPx: 11,
  nameFontSize: "12px",
  statusFontSize: "8.5px",
  statusGapPx: 2,
  descriptionFontSize: "10.5px",
  descriptionLineHeight: 1.5,
  descriptionGapPx: 4,
  descriptionMaxWidthEm: 44,
}

// Tab 24 "red-plate" — the whole detail inverts into a solid dark-red
// rectangle (the in-discussion box red, HUD87_POLICY_STATUS_APPEARANCE), all
// text and the mini square in white. The plate hugs its content up to a
// reading measure.
export const POLICY_DETAIL_RED_PLATE_STYLE = {
  background: "#8B0000",
  textColor: "#fff",
  padding: "9px 12px 10px",
  borderRadiusPx: 6,
  maxWidthPx: 460,
  nameFontSize: "11.5px",
  descriptionFontSize: "10.5px",
  descriptionLineHeight: 1.5,
  descriptionGapPx: 4,
  // The mini square recolors to read on the plate: white outline, white icon.
  squareAppearance: {
    fillColor: "transparent",
    borderColor: "#fff",
    borderStyle: "solid",
    iconColor: "#fff",
  } as PolicyItemAppearance,
}

// Tab 25 "more-text" — the stock layout with a status line under the name and
// a SECOND sentence (POLICY_EXTENDED_DESCRIPTIONS) appended to the
// description, at a slightly larger reading size. Its status-placement
// siblings share these knobs: tab 30 "more-text-no-status" drops the status
// line entirely; tab 31 "more-text-status-top" moves it ABOVE the name row
// as an eyebrow.
export const POLICY_DETAIL_MORE_TEXT_STYLE = {
  statusFontSize: "8.5px",
  statusGapPx: 1,
}

// Tab 26 "headline" — the power breakdown's title voice: the name at the
// breakdown-title size with the status right-aligned on the same line
// (echoing tab 14's definition-inline), a full-width red rule under the title
// row (echoing its "underline" column emphasis), description below.
export const POLICY_DETAIL_HEADLINE_STYLE = {
  nameFontSize: "13px",
  nameLetterSpacing: "0.04em",
  statusFontSize: "9px",
  ruleWidthPx: 2,
  ruleColor: "var(--accent)",
  ruleGapAbovePx: 4,
  descriptionFontSize: "10.5px",
  descriptionLineHeight: 1.5,
  descriptionGapPx: 6,
  descriptionMaxWidthEm: 44,
}

// Tab 27 "red-frame" — the stock content inside a red rounded outline (the
// power breakdown's "frame" emphasis at detail scale); text stays black, the
// frame hugs its content up to a reading measure.
export const POLICY_DETAIL_RED_FRAME_STYLE = {
  borderWidthPx: 1.5,
  borderColor: "var(--accent)",
  borderRadiusPx: 4,
  padding: "8px 10px 9px",
  maxWidthPx: 460,
  nameFontSize: "11px",
  descriptionFontSize: "10px",
  descriptionLineHeight: 1.45,
  descriptionGapPx: 2,
}

type PolicyHoverSwapContextValue = {
  hoveredPolicyId: string | null
  setHoveredPolicyId: (policyId: string | null) => void
  // A hovered STATUS LABEL ("Current Policy", …) — the below-the-HUD detail
  // slot (belowHudDetailSwap.tsx) lists every policy grouped by section while
  // one is set. Independent of hoveredPolicyId; the usual cross-clears keep
  // at most one of the two active.
  hoveredStatus: PolicyStatus | null
  setHoveredStatus: (status: PolicyStatus | null) => void
  // Per-variant render treatment for the hover detail (see
  // PolicyDetailTreatment above); undefined = stock.
  detailTreatment?: PolicyDetailTreatment
  // Tabs 7–11: the below-HUD slot renders the all-policies MAP for every
  // hover in the policy area (squares AND status labels) instead of the
  // single-policy detail / by-section list — see policyMapHoverDetail.tsx.
  mapDesign?: PolicyMapDesignId
  // Tabs 23–27: how the grid square under the pointer (and the hovered status
  // label) highlights itself — see the POLICY_SQUARE_* consts in
  // policyAppearance.ts. Read by PolicyItem / PoliciesSection directly.
  squareHighlight?: PolicySquareHighlight
  // Tabs 23–27: the status labels ("Current Policy" …) are NOT hover targets —
  // no red label, no by-section takeover; only the squares hover.
  labelHoverDisabled?: boolean
  // "hud0-lean-status-underline" tab: the whole status ROW (label, gaps,
  // empty run — everywhere not on a policy square) is the label-hover
  // target, and the hovered label renders bold + red. Read by
  // PoliciesSection.
  statusRowHover?: boolean
}

const PolicyHoverSwapContext = createContext<PolicyHoverSwapContextValue | null>(null)

// Null outside a <PolicyHoverSwap>. PoliciesPanel reads this to decide whether
// hover should open its floating tooltip (no provider) or instead be reported
// here so a PolicyHoverSwapTarget can render the detail on the HUD canvas.
export const usePolicyHoverSwap = () => useContext(PolicyHoverSwapContext)

// Renders no DOM of its own, so wrapping existing flex/grid children does not
// disturb their layout relationship to the parent container.
export const PolicyHoverSwap = ({
  children,
  detailTreatment,
  mapDesign,
  squareHighlight,
  labelHoverDisabled,
  statusRowHover,
}: {
  children: ReactNode
  detailTreatment?: PolicyDetailTreatment
  mapDesign?: PolicyMapDesignId
  squareHighlight?: PolicySquareHighlight
  labelHoverDisabled?: boolean
  statusRowHover?: boolean
}) => {
  const [hoveredPolicyId, setHoveredPolicyId] = useState<string | null>(null)
  const [hoveredStatus, setHoveredStatus] = useState<PolicyStatus | null>(null)
  // Hover is not an input on this surface (mobile dashboard) — provide
  // null so every consumer renders its base, unhovered content.
  const hoverSwapsDisabled = useHoverSwapsDisabled()
  return (
    <PolicyHoverSwapContext.Provider
      value={hoverSwapsDisabled ? null : {
        hoveredPolicyId,
        setHoveredPolicyId,
        hoveredStatus,
        setHoveredStatus,
        detailTreatment,
        mapDesign,
        squareHighlight,
        labelHoverDisabled,
        statusRowHover,
      }}
    >
      {children}
    </PolicyHoverSwapContext.Provider>
  )
}

// Size of the mini policy square rendered beside the policy name in the
// detail — a slightly smaller echo of the 16px panel squares, in the same
// per-status treatment (HUD87_POLICY_STATUS_APPEARANCE) with the policy's own
// icon, so the detail visibly connects back to the hovered square.
const POLICY_HOVER_SQUARE_SIZE_PX = 14
const POLICY_HOVER_SQUARE_RADIUS_PX = 4
const POLICY_HOVER_SQUARE_ICON_SIZE_PX = 9

export const PolicyMiniSquare = ({
  policy,
  status,
  appearance: appearanceOverride,
}: {
  policy: Policy
  status: PolicyStatus
  // Recolors the square wholesale (e.g. white-on-red on tab 24's plate);
  // default is the policy's own per-status treatment.
  appearance?: PolicyItemAppearance
}) => {
  const appearance = appearanceOverride ?? HUD87_POLICY_STATUS_APPEARANCE[status]
  const IconComponent = policy.icon ? POLICY_ICONS[policy.icon] : undefined
  return (
    <span
      className="inline-flex items-center justify-center shrink-0"
      style={{
        width: POLICY_HOVER_SQUARE_SIZE_PX,
        height: POLICY_HOVER_SQUARE_SIZE_PX,
        borderRadius: POLICY_HOVER_SQUARE_RADIUS_PX,
        background: appearance.fillColor,
        border: `1.5px ${appearance.borderStyle} ${appearance.borderColor}`,
        color: appearance.iconColor,
      }}
    >
      {IconComponent && (
        <IconComponent size={POLICY_HOVER_SQUARE_ICON_SIZE_PX} strokeWidth={2.2} />
      )}
    </span>
  )
}

// The hovered policy's name and one-line layperson description, styled to
// sit directly on the HUD canvas (no box, no shadow). Name and description
// both line-wrap as needed so the full text always shows — nothing is
// ellipsis-truncated. A mini square in the policy's own per-status treatment
// (icon included, when the policy has one) sits beside the name, tying the
// detail back to the hovered square. Exported for the below-the-HUD detail
// slot (belowHudDetailSwap.tsx) as well as PolicyHoverSwapTarget below.
export const PolicyHoverDetail = ({ policyId }: { policyId: string }) => {
  const swap = usePolicyHoverSwap()
  const treatment = swap?.detailTreatment
  const grouped = useGroupedPoliciesForActiveDate()
  const status = POLICY_STATUS_ORDER.find((s) =>
    grouped[s].some((p) => p.id === policyId),
  )
  const policy = status
    ? grouped[status].find((p) => p.id === policyId)
    : undefined
  const description = POLICY_DESCRIPTIONS[policyId]
  if (!policy || !status) return null

  if (treatment === "big-square-aside") {
    const style = POLICY_DETAIL_BIG_SQUARE_STYLE
    const appearance = HUD87_POLICY_STATUS_APPEARANCE[status]
    const IconComponent = policy.icon ? POLICY_ICONS[policy.icon] : undefined
    return (
      <div className="flex items-start" style={{ gap: style.textGapPx }}>
        <span
          className="flex items-center justify-center shrink-0"
          style={{
            width: style.squareSizePx,
            height: style.squareSizePx,
            borderRadius: style.squareRadiusPx,
            background: appearance.fillColor,
            border: `${style.squareStrokeWidthPx}px ${appearance.borderStyle} ${appearance.borderColor}`,
            color: appearance.iconColor,
          }}
        >
          {IconComponent && (
            <IconComponent
              size={style.squareIconSizePx}
              strokeWidth={style.squareIconStrokeWidth}
            />
          )}
        </span>
        <div>
          <div
            className="font-mono font-bold"
            style={{ fontSize: style.nameFontSize }}
          >
            {policy.label}
          </div>
          <div
            className="font-mono"
            style={{ fontSize: style.statusFontSize, marginTop: style.statusGapPx }}
          >
            {POLICY_STATUS_LABELS[status]}
          </div>
          {description && (
            <div
              className="font-sans"
              style={{
                fontSize: style.descriptionFontSize,
                lineHeight: style.descriptionLineHeight,
                marginTop: style.descriptionGapPx,
                maxWidth: `${style.descriptionMaxWidthEm}em`,
              }}
            >
              {description}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (treatment === "red-plate") {
    const style = POLICY_DETAIL_RED_PLATE_STYLE
    return (
      <div
        style={{
          background: style.background,
          color: style.textColor,
          borderRadius: style.borderRadiusPx,
          padding: style.padding,
          width: "fit-content",
          maxWidth: style.maxWidthPx,
        }}
      >
        <div className="flex items-start gap-1.5">
          <PolicyMiniSquare
            policy={policy}
            status={status}
            appearance={style.squareAppearance}
          />
          <div
            className="font-mono font-bold"
            style={{ fontSize: style.nameFontSize }}
          >
            {policy.label}
          </div>
        </div>
        {description && (
          <div
            className="font-sans"
            style={{
              fontSize: style.descriptionFontSize,
              lineHeight: style.descriptionLineHeight,
              marginTop: style.descriptionGapPx,
            }}
          >
            {description}
          </div>
        )}
      </div>
    )
  }

  if (
    treatment === "more-text" ||
    treatment === "more-text-no-status" ||
    treatment === "more-text-status-top"
  ) {
    const style = POLICY_DETAIL_MORE_TEXT_STYLE
    const fullDescription = [description, POLICY_EXTENDED_DESCRIPTIONS[policyId]]
      .filter(Boolean)
      .join(" ")
    return (
      <div>
        {/* Tab 31: the status line as an eyebrow ABOVE the name row. */}
        {treatment === "more-text-status-top" && (
          <div className="font-mono text-[8.5px] mb-1.5">
            {POLICY_STATUS_LABELS[status]}
          </div>
        )}
        <div className="flex items-start gap-1.5">
          <PolicyMiniSquare policy={policy} status={status} />
          <div>
            <HudHoverTitle>{policy.label}</HudHoverTitle>
            {treatment === "more-text" && (
              <div
                className="font-mono"
                style={{ fontSize: style.statusFontSize, marginTop: style.statusGapPx }}
              >
                {POLICY_STATUS_LABELS[status]}
              </div>
            )}
          </div>
        </div>
        {fullDescription && (
          <HudHoverDescription className="max-w-[46em]">
            {fullDescription}
          </HudHoverDescription>
        )}
      </div>
    )
  }

  if (treatment === "headline") {
    const style = POLICY_DETAIL_HEADLINE_STYLE
    return (
      <div>
        <div className="flex items-baseline justify-between gap-3">
          <div className="flex items-start gap-1.5">
            <PolicyMiniSquare policy={policy} status={status} />
            <span
              className="font-mono font-bold"
              style={{
                fontSize: style.nameFontSize,
                letterSpacing: style.nameLetterSpacing,
              }}
            >
              {policy.label}
            </span>
          </div>
          <span
            className="font-mono whitespace-nowrap"
            style={{ fontSize: style.statusFontSize }}
          >
            {POLICY_STATUS_LABELS[status]}
          </span>
        </div>
        <div
          style={{
            height: style.ruleWidthPx,
            background: style.ruleColor,
            marginTop: style.ruleGapAbovePx,
          }}
        />
        {description && (
          <div
            className="font-sans"
            style={{
              fontSize: style.descriptionFontSize,
              lineHeight: style.descriptionLineHeight,
              marginTop: style.descriptionGapPx,
              maxWidth: `${style.descriptionMaxWidthEm}em`,
            }}
          >
            {description}
          </div>
        )}
      </div>
    )
  }

  if (treatment === "red-frame") {
    const style = POLICY_DETAIL_RED_FRAME_STYLE
    return (
      <div
        style={{
          border: `${style.borderWidthPx}px solid ${style.borderColor}`,
          borderRadius: style.borderRadiusPx,
          padding: style.padding,
          width: "fit-content",
          maxWidth: style.maxWidthPx,
        }}
      >
        <div className="flex items-start gap-1.5">
          <PolicyMiniSquare policy={policy} status={status} />
          <div
            className="font-mono font-bold"
            style={{ fontSize: style.nameFontSize }}
          >
            {policy.label}
          </div>
        </div>
        {description && (
          <div
            className="font-sans"
            style={{
              fontSize: style.descriptionFontSize,
              lineHeight: style.descriptionLineHeight,
              marginTop: style.descriptionGapPx,
            }}
          >
            {description}
          </div>
        )}
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-start gap-1.5">
        <PolicyMiniSquare policy={policy} status={status} />
        <div
          className="font-mono font-bold"
          style={{ fontSize: POLICY_HOVER_DETAIL_STYLE.nameFontSize }}
        >
          {policy.label}
        </div>
      </div>
      {description && (
        <div
          className="font-sans mt-0.5"
          style={{
            fontSize: POLICY_HOVER_DETAIL_STYLE.descriptionFontSize,
            lineHeight: POLICY_HOVER_DETAIL_STYLE.descriptionLineHeight,
          }}
        >
          {description}
        </div>
      )}
    </div>
  )
}

// Wraps the HUD block whose spot the hovered policy's detail takes over.
// While a policy is hovered the children keep their layout footprint but go
// visibility-hidden, and the overlay paints over them (it may run a little
// past the slot's bottom edge — accepted for now so the rest of the canvas
// never reflows on hover). Further overlay modes (compact full-policy list
// under or instead of the detail) live in the aesthetic-options archive.
export const PolicyHoverSwapTarget = ({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) => {
  const swap = usePolicyHoverSwap()
  const sequentialFade = useHoverSwapSequentialFade()
  const hoveredPolicyId = swap?.hoveredPolicyId ?? null
  return (
    <div className={`relative ${className ?? ""}`}>
      {/* Hidden via opacity (not visibility) so the treemap underneath stays
          HIT-TESTABLE while the detail covers it: a pointer entering the
          column lands directly on a cell and swaps detail → takeover in one
          event. Undefined when idle so an outer swap target's hiding still
          inherits. */}
      <div style={hoverSwapHiddenContentStyle(Boolean(hoveredPolicyId), sequentialFade)}>
        {children}
      </div>
      {/* pointer-events-none: the detail never intercepts the pointer — the
          (invisible) treemap underneath handles the arrival hover. */}
      <HoverSwapFadeOverlay
        active={Boolean(hoveredPolicyId)}
        contentKey={hoveredPolicyId ?? undefined}
        className="absolute top-0 left-0 right-0 z-10 pointer-events-none"
      >
        {hoveredPolicyId && <PolicyHoverDetail policyId={hoveredPolicyId} />}
      </HoverSwapFadeOverlay>
    </div>
  )
}
